import { Alert, Card, Flex, Skeleton, Tabs } from 'antd';
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  CalendarSlot,
  PreSalesBookingChannel,
  PreSalesBookingLanes,
  Result,
  Slot,
} from '../../../dto/model';
import { strings } from '../../../lang';
import { heatBookingPageService } from '../../../services/heat-booking-page-service';
import { salesAppointmentsService } from '../../../services/sales-appointments-service';
import { SalesCallCalendar } from '../../appointment-booking/sales-call-calendar';
import { PreSalesQuestionnaireCustomer, PreSalesZeegBookingHandler } from './pre-sales-questionnaire-types';
import { getBookingLaneLabel, resolveZeegBookingLinksInfo, ZeegBookingCalendarInfo } from './pre-sales-questionnaire-utils';
import { BookingLaneReasonResult, PreSalesBookingLaneReasonModal } from './pre-sales-booking-lane-reason-modal';
import { usePreSalesBookingLaneCalendar, PreSalesBookingLaneCalendar } from './use-pre-sales-booking-lane-calendar';
import { usePreSalesFieldSalesBooking } from './use-pre-sales-field-sales-booking';
import { usePreSalesVideoSalesBooking } from './use-pre-sales-video-sales-booking';

type PreSalesBookingLaneBookingProps = {
  signupId?: string;
  crmId?: string;
  zipCode?: string;
  customer?: PreSalesQuestionnaireCustomer;
  lanes: string[];
  /** Unified-flow video lanes book through the ad page so the case is completed after a successful booking. */
  bookZeegSlot?: PreSalesZeegBookingHandler;
  onBooked?: (slot?: Slot, lane?: string, reason?: BookingLaneReasonResult) => void | Promise<void>;
};

/**
 * The PreSales booking channel a lane is booked through: field sales for the on-site lane, insulation for the
 * insulation-only lane, video sales for everything else. Mirrors the backend `BookingLanes.BookingChannelFor`.
 */
const laneBookingChannel = (lane: string): PreSalesBookingChannel => {
  if (lane === PreSalesBookingLanes.HpHv) return 'FieldSales';
  if (lane === PreSalesBookingLanes.InVideo) return 'Insulation';
  return 'VideoSales';
};

/** A booking the agent requested in a non-Priority-1 lane, held until they give a reason. */
type PendingBookingLaneRequest = {
  lane: string;
  slot: CalendarSlot;
  token: string;
};

/**
 * Unified-flow (type=ad) booking step. Renders one tab per eligible lane (ordered by the lane priority
 * returned on submit) and shows the calendar for the active lane. The first lane is selected by default
 * and drives the initial calendar request.
 *
 * Booking a lane other than the first one asks the agent why, before the booking is sent, so the reason
 * travels with the completion payload. Cancelling that prompt aborts the booking.
 */
export const PreSalesBookingLaneBooking = ({
  signupId,
  crmId,
  zipCode,
  customer,
  lanes,
  bookZeegSlot,
  onBooked,
}: PreSalesBookingLaneBookingProps) => {
  // The backend already restricts the offered lanes to the released set, so render exactly what it sent
  // rather than a hardcoded pair. A stale or malformed response is still guarded by the empty check below.
  const releasedBookingLanes = useMemo(() => lanes.filter(Boolean), [lanes]);
  const laneCalendar = usePreSalesBookingLaneCalendar({ signupId, lanes: releasedBookingLanes });
  const activeBookingLane = laneCalendar.activeBookingLane ?? releasedBookingLanes[0];
  const laneResult: PreSalesBookingLaneCalendar | null = laneCalendar.result;
  const firstBookingLane = releasedBookingLanes[0];

  // Non-null while the reason prompt is open. Holds the slot the agent clicked so the booking can resume.
  const [pendingBooking, setPendingBooking] = useState<PendingBookingLaneRequest | null>(null);
  // Resolves the promise the calendar is awaiting while the prompt is open, so the calendar stays in its
  // normal booking state instead of being told the booking failed.
  const pendingResolveRef = useRef<((result: Result<Slot>) => void) | null>(null);

  const zeegBookingLinkInfo: ZeegBookingCalendarInfo | null = useMemo(() => {
    if (laneResult?.lane !== activeBookingLane) return null;

    const linksInfo = resolveZeegBookingLinksInfo(laneResult?.bookingLinks);
    return linksInfo?.primary ?? null;
  }, [activeBookingLane, laneResult]);

  const videoSales = usePreSalesVideoSalesBooking({
    crmId,
    signupId,
    customer,
    zeegBookingLinkInfo,
    bookingLane: activeBookingLane,
    eligibleBookingLanes: releasedBookingLanes,
  });

  // The field-sales lane books through PreSales against the signup id, since the unified flow has no deal.
  const fieldSales = usePreSalesFieldSalesBooking({
    crmId,
    dealId: signupId,
    zipCode,
  });

  const handleBooking = useCallback((isSuccess: boolean, slot?: Slot, error?: string) => {
    if (isSuccess && slot) return;
    // The hook that owns the active lane reports its own result; the other one must not also show an error.
    if (activeBookingLane === PreSalesBookingLanes.HpHv) {
      fieldSales.handleBooking(isSuccess, slot, error);
      return;
    }
    videoSales.handleBooking(isSuccess, slot, error);
  }, [activeBookingLane, fieldSales, videoSales]);

  /** Sends the booking for the active lane, attaching the reason when the agent supplied one. */
  const submitBooking = useCallback((
    slot: CalendarSlot,
    token: string,
    lane: string,
    reason?: BookingLaneReasonResult,
  ): Promise<Result<Slot>> => {
    if (lane === PreSalesBookingLanes.HpHv) {
      return fieldSales.bookSlot(slot, token).then(async (result) => {
        if (result.isSuccess && signupId) {
          await heatBookingPageService.recordPreSalesBookingChannel(
            { signupId },
            laneBookingChannel(lane),
            token,
          );
          await onBooked?.(result.data, lane, reason);
        }
        return result;
      });
    }

    if (laneResult?.lane === lane && laneResult.calendarProvider === 'BookingOs' && signupId) {
      return salesAppointmentsService.bookSlot(
        '',
        signupId,
        slot.startDate,
        token,
        'presales-lane',
        'SalesCall1Lead',
        undefined,
        lane,
        reason?.reason,
      ).then(async (result) => {
        if (result.isSuccess) {
          await heatBookingPageService.recordPreSalesBookingChannel(
            { signupId },
            laneBookingChannel(lane),
            token,
          );
          await onBooked?.(result.data, lane, reason);
        }
        return result;
      });
    }

    // The shared video-sales hook records the channel itself after a successful booking, so only the
    // injected ad-page handler needs it recorded here.
    if (!bookZeegSlot || !zeegBookingLinkInfo) {
      return videoSales.bookSlot(slot, token, reason?.reason);
    }

    return bookZeegSlot(slot, token, zeegBookingLinkInfo, lane, reason, releasedBookingLanes).then(async (result) => {
      if (result.isSuccess && signupId) {
        await heatBookingPageService.recordPreSalesBookingChannel(
          { signupId },
          laneBookingChannel(lane),
          token,
        );
      }
      return result;
    });
  }, [bookZeegSlot, fieldSales, laneResult, onBooked, releasedBookingLanes, signupId, videoSales, zeegBookingLinkInfo]);

  const bookSlot = useCallback((slot: CalendarSlot, token: string): Promise<Result<Slot>> => {
    // A booking in the first offered lane needs no explanation; any other lane does.
    if (activeBookingLane && firstBookingLane && activeBookingLane !== firstBookingLane) {
      // Hold the calendar's promise open: it stays in its booking state, and the prompt decides the outcome.
      return new Promise<Result<Slot>>((resolve) => {
        pendingResolveRef.current = resolve;
        setPendingBooking({ lane: activeBookingLane, slot, token });
      });
    }

    return submitBooking(slot, token, activeBookingLane ?? '');
  }, [activeBookingLane, firstBookingLane, submitBooking]);

  const confirmReason = useCallback((reason: BookingLaneReasonResult) => {
    const pending = pendingBooking;
    const resolve = pendingResolveRef.current;
    pendingResolveRef.current = null;
    setPendingBooking(null);
    if (!pending) {
      resolve?.({ isSuccess: false, errors: [], data: {} as Slot });
      return;
    }

    void submitBooking(pending.slot, pending.token, pending.lane, reason)
      .then((result) => {
        // The calendar calls onBooking itself once the promise settles, so only the result is returned here.
        resolve?.(result);
      });
  }, [pendingBooking, submitBooking]);

  const cancelReason = useCallback(() => {
    const resolve = pendingResolveRef.current;
    pendingResolveRef.current = null;
    setPendingBooking(null);
    // Cancelling aborts the booking: the calendar is told it did not happen, and nothing is sent.
    resolve?.({ isSuccess: false, errors: [], data: {} as Slot });
  }, []);

  if (!releasedBookingLanes.length) {
    return null;
  }

  const renderCalendar = () => {
    if (laneCalendar.isLoading && !laneResult) {
      return <Skeleton active />;
    }

    if (laneCalendar.error) {
      return <Alert type="error" showIcon message={laneCalendar.error} />;
    }

    if (!activeBookingLane) {
      return null;
    }

    const isActiveZeegResult = laneResult?.lane === activeBookingLane && laneResult.calendarProvider === 'Zeeg';
    if (isActiveZeegResult) {
      const hasZeegRouting = Boolean(zeegBookingLinkInfo?.ownerSlug && zeegBookingLinkInfo.eventTypeSlug);
      // The lane result (and its Zeeg booking links) is only fetched once SalesCallCalendar mounts and
      // calls loadSlots, so missing routing is only an error after the result has actually loaded.
      if (!hasZeegRouting) {
        return <Alert type="error" showIcon message={strings.preSalesQuestionnaire.videoBookingConfigurationError} />;
      }
    }

    // Zeeg (once routing is confirmed above) and every other provider render the same calendar; only the
    // Zeeg-routing error above needs to special-case the provider.
    return (
      <SalesCallCalendar
        key={activeBookingLane}
        opportunityId=""
        signupId=""
        sourceSystemHeader="presales-lane"
        skipConfirmationAfterBooking
        loadSlots={laneCalendar.loadSlots}
        bookSlot={bookSlot}
        onBooking={handleBooking}
      />
    );
  };

  return (
    <Card size="small" className="bp-presales-lane-booking">
      <Flex vertical gap={12}>
        <Tabs
          activeKey={activeBookingLane}
          onChange={laneCalendar.selectBookingLane}
          items={releasedBookingLanes.map((lane) => ({ key: lane, label: getBookingLaneLabel(lane) }))}
        />
        {renderCalendar()}
      </Flex>
      <PreSalesBookingLaneReasonModal
        open={Boolean(pendingBooking)}
        bookedBookingLane={pendingBooking?.lane ?? ''}
        firstBookingLane={firstBookingLane ?? ''}
        onCancel={cancelReason}
        onConfirm={confirmReason}
      />
    </Card>
  );
};
